import React, { useState } from 'react';
import { useLocale } from 'locale';
import * as Styled from './styled';
import { chunk, flatten, noop } from 'lodash';
import { Pagination } from 'ds/components/pagination';
import { ListingProps, tabsIcons, TabType } from '../';
import { AgentOfficeBulletinCard } from 'screens/AgentOfficePages/common/components/Card';
import { Section } from 'components/navigation/subheader/SectionsNavigation';
import { IAgentOfficeBulletin } from 'utils/entities';
import withEvents from 'analytics/withEvents';
import { Action, Direction, EventProps } from 'screens/AgentOfficePages/types';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';

interface TabContent {
  pages: IAgentOfficeBulletin[][];
  page: number;
  tab: TabType;
}

interface Tab {
  tabType: TabType;
  count: number;
  isActive: boolean;
  onClick: (tabIdx: number) => void;
  tabIdx: number;
}

const Tab: React.FC<Tab> = ({ tabType, count, isActive, onClick, tabIdx }) => {
  const { t } = useLocale();
  return (
    <Styled.TabWrapper isActive={isActive} onClick={() => onClick(tabIdx)}>
      {tabsIcons[tabType]}
      <Styled.Title isActive={isActive} dangerouslySetInnerHTML={{ __html: t('officePage.listing.tabs.title', { tabType, count }) }} />
    </Styled.TabWrapper>
  );
};

const LISTING_LIMIT = 8;


const DesktopListingBase: React.FC<ListingProps> = ({ bulletinsDict, onCarouselScroll = noop, pageDetails }) => {
  const { t } = useLocale();
  const tabsOrder = [ TabType.Sale, TabType.Rent, TabType.CommercialSale, TabType.CommercialRent ];
  const tabs = React.useMemo(() => tabsOrder.map<TabContent>((tab) => bulletinsDict[tab] && bulletinsDict[tab].length ? { pages: chunk(bulletinsDict[tab], LISTING_LIMIT), page: 1, tab } : null).filter(Boolean), [ bulletinsDict ]);

  const [ tabsContentStore, setTabsContentStore ] = useState<TabContent[]>(tabs);
  const [ activeTab, setActiveTab ] = useState(0);

  const handleTabChange = (tabIdx: number) => {
    setActiveTab(tabIdx);
  };

  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = isOfficePage ? 'office_carousel_scroll' : 'agent_carousel_scroll';
  const category = isOfficePage ? 'office' : 'agent';
  const eventHandler = (direction: Direction) => {
    onCarouselScroll({
      event,
      category,
      direction,
      action: 'click' as Action,
      pageDetails,
    });
  };

  const handlePageChange = (tabIdx: number, pageIdx: number) => {
    setTabsContentStore((prevState) => {
      const page = prevState[tabIdx].page;
      const direction = page !== pageIdx ? (page > pageIdx ? Direction.Right : Direction.Left) : null;
      if (direction) {
        eventHandler(direction);
      }
      const nextState = [ ...prevState ];
      const newTabState = { ...nextState[tabIdx], page: pageIdx };
      nextState[tabIdx] = newTabState;
      return nextState;
    });
  };

  const currentPages = tabsContentStore[activeTab].pages;
  const totalPages = currentPages.length;
  const activePage = tabsContentStore[activeTab].page;
  const count = React.useMemo(() => tabsContentStore.reduce((acc, k) => acc + flatten(k.pages).length, 0), [ bulletinsDict ]);

  return (
    <Styled.Wrapper>
      <Styled.AreaTitle>{t('officePage.subMenu.item', { item: Section.LISTINGS })} · <span>{count}</span></Styled.AreaTitle>
      <Styled.NavWrapper>
        <Styled.TabsSection>
          {tabsContentStore.map((tabState: TabContent, i) => (
            <Tab
              key={i}
              tabType={tabState.tab}
              count={flatten(tabState.pages).length}
              isActive={i === activeTab}
              onClick={handleTabChange}
              tabIdx={i}
            />
          ))}
        </Styled.TabsSection>
        <Styled.PageNavSection>
          {totalPages > 1 ? (
            <Pagination
              totalPages={totalPages}
              onSetActivePage={(pageIdx) => handlePageChange(activeTab, pageIdx)}
              activePage={activePage}
              paginationType={'pages'}
              maxPagesToShow={5}
            />
          ) : null}
        </Styled.PageNavSection>
      </Styled.NavWrapper>
      {totalPages ? (
        <Styled.List>
          {currentPages[activePage - 1].map((l, i) => <AgentOfficeBulletinCard key={i} poi={l} />)}
        </Styled.List>
      ) : null}
    </Styled.Wrapper>
  );
};

export const DesktopListing = withEvents<ListingProps>(sendEvent => ({
  onCarouselScroll(props: EventProps) {
    sendEvent(props.event, props.category, {
      ...props.pageDetails,
      event: {
        carousel_type: 'active_listings',
        direction: props.direction,
        action: props.action,
      },
    });
  },
}))(DesktopListingBase);
